// Vanilla Canvas2D renderer for the "Fireflies in the Bushes" template.
// Deliberately framework-free: this same module is meant to be reusable
// as-is inside a static exported HTML file (Canvas Despatch), not just
// inside the React admin/participant pages.
//
// Usage:
//   const stage = createFirefliesStage(canvasEl);
//   stage.setMessages([{ id, sender_name, message_body }, ...]);
//   stage.onSelect((msg, screenPoint) => { ... });
//   stage.resize(); // call on container resize
//   stage.destroy();

function seededRandom(seed) {
  let t = seed + 0x6D2B79F5;
  return function () {
    t = (t + 0x6D2B79F5) | 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function hashId(id) {
  let h = 0;
  const s = String(id);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h >>> 0;
}

function drawLeaf(ctx, x, y, len, angle, color) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.quadraticCurveTo(len * 0.45, -len * 0.38, len, 0);
  ctx.quadraticCurveTo(len * 0.45, len * 0.38, 0, 0);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = 'rgba(20, 40, 18, 0.18)';
  ctx.lineWidth = 0.6;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(len * 0.85, 0);
  ctx.stroke();
  ctx.restore();
}

function drawMoonWithCraters(ctx, x, y, r) {
  const moonGlow = ctx.createRadialGradient(x, y, 0, x, y, r * 3.5);
    moonGlow.addColorStop(0, 'rgba(255, 186, 90, 0.42)');
    moonGlow.addColorStop(0.45, 'rgba(210, 120, 40, 0.12)');
    moonGlow.addColorStop(1, 'rgba(180, 90, 20, 0)');
    ctx.fillStyle = moonGlow;
    ctx.fillRect(x - r * 3.5, y - r * 3.5, r * 7, r * 7);

    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    const moonGrad = ctx.createRadialGradient(x - r * 0.28, y - r * 0.35, r * 0.08, x, y, r);
    moonGrad.addColorStop(0, '#ffe7a0');
    moonGrad.addColorStop(0.28, '#f0c15a');
    moonGrad.addColorStop(0.62, '#d4782a');
    moonGrad.addColorStop(1, '#8a3a18');
    ctx.fillStyle = moonGrad;
    ctx.fill();

  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.clip();

  const maria = [
    { dx: 0.18, dy: 0.12, rx: 0.42, ry: 0.28, a: 0.08 },
    { dx: -0.22, dy: -0.08, rx: 0.28, ry: 0.2, a: 0.07 },
    { dx: 0.08, dy: -0.28, rx: 0.18, ry: 0.12, a: 0.06 },
  ];
  for (const m of maria) {
    ctx.beginPath();
    ctx.ellipse(x + m.dx * r, y + m.dy * r, r * m.rx, r * m.ry, 0.3, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(120, 70, 28, ${m.a + 0.12})`;
    ctx.fill();
  }

  const craters = [
    { dx: -0.28, dy: -0.18, size: 0.2 },
    { dx: 0.32, dy: -0.22, size: 0.16 },
    { dx: -0.12, dy: 0.3, size: 0.14 },
    { dx: 0.22, dy: 0.22, size: 0.1 },
    { dx: -0.38, dy: 0.08, size: 0.09 },
    { dx: 0.08, dy: -0.38, size: 0.07 },
    { dx: 0.02, dy: 0.02, size: 0.055 },
    { dx: 0.4, dy: 0.05, size: 0.05 },
  ];
  for (const c of craters) {
    const cx = x + c.dx * r;
    const cy = y + c.dy * r;
    const cr = r * c.size;
    const craterGrad = ctx.createRadialGradient(cx - cr * 0.25, cy - cr * 0.25, cr * 0.15, cx, cy, cr);
    craterGrad.addColorStop(0, 'rgba(90, 40, 16, 0.45)');
    craterGrad.addColorStop(0.55, 'rgba(140, 70, 28, 0.22)');
    craterGrad.addColorStop(1, 'rgba(200, 130, 50, 0.06)');
    ctx.fillStyle = craterGrad;
    ctx.beginPath();
    ctx.arc(cx, cy, cr, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx + cr * 0.18, cy + cr * 0.16, cr * 0.72, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255, 252, 245, 0.12)';
    ctx.lineWidth = Math.max(0.6, cr * 0.12);
    ctx.stroke();
  }
  ctx.restore();
}

function drawTree(ctx, x, y, height, width, t) {
  const trunkWidth = width * 0.1;
  const trunkHeight = height * 0.6;
  const crownR = width * 0.5;

  // Trunk with realistic bark texture
  const trunkGrad = ctx.createLinearGradient(x - trunkWidth / 2, y, x + trunkWidth / 2, y);
  trunkGrad.addColorStop(0, '#1a0f08');
  trunkGrad.addColorStop(0.2, '#2d1a0f');
  trunkGrad.addColorStop(0.4, '#3d2814');
  trunkGrad.addColorStop(0.6, '#4a3018');
  trunkGrad.addColorStop(0.8, '#352210');
  trunkGrad.addColorStop(1, '#1a0f08');
  ctx.fillStyle = trunkGrad;
  ctx.beginPath();
  ctx.moveTo(x - trunkWidth * 0.55, y);
  ctx.lineTo(x - trunkWidth * 0.3, y - trunkHeight);
  ctx.lineTo(x + trunkWidth * 0.25, y - trunkHeight);
  ctx.lineTo(x + trunkWidth * 0.6, y);
  ctx.closePath();
  ctx.fill();

  // Bark texture lines
  ctx.strokeStyle = 'rgba(12, 8, 4, 0.5)';
  ctx.lineWidth = 1;
  for (let i = 0; i < 8; i++) {
    const tx = x - trunkWidth * 0.45 + i * trunkWidth * 0.13;
    ctx.beginPath();
    ctx.moveTo(tx, y - 2);
    ctx.quadraticCurveTo(tx + Math.sin(t * 0.15 + i * 1.3) * 3, y - trunkHeight * 0.5, tx + Math.sin(t * 0.15 + i) * 2, y - trunkHeight);
    ctx.stroke();
  }

  // Major limbs with organic curves
  const limbs = [
    { ang: -2.4, len: trunkHeight * 0.6, w: 8, taper: 0.15 },
    { ang: -2.0, len: trunkHeight * 0.75, w: 9, taper: 0.12 },
    { ang: -1.55, len: trunkHeight * 0.65, w: 7, taper: 0.18 },
    { ang: -1.1, len: trunkHeight * 0.7, w: 8, taper: 0.15 },
    { ang: -0.65, len: trunkHeight * 0.55, w: 6, taper: 0.2 },
  ];
  for (const limb of limbs) {
    const sway = Math.sin(t * 0.25 + limb.ang) * 0.03;
    const limbGrad = ctx.createLinearGradient(x, y - trunkHeight * 0.9,
      x + Math.cos(limb.ang + sway) * limb.len,
      y - trunkHeight * 0.9 + Math.sin(limb.ang + sway) * limb.len * 0.85);
    limbGrad.addColorStop(0, '#2d1b0f');
    limbGrad.addColorStop(1, '#181008');
    ctx.strokeStyle = limbGrad;
    ctx.lineWidth = limb.w;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x, y - trunkHeight * 0.92);
    // Organic curve with multiple control points
    const cp1x = x + Math.cos(limb.ang + sway) * limb.len * 0.35;
    const cp1y = y - trunkHeight * 0.92 + Math.sin(limb.ang + sway) * limb.len * 0.25;
    const cp2x = x + Math.cos(limb.ang + sway) * limb.len * 0.7;
    const cp2y = y - trunkHeight * 0.92 + Math.sin(limb.ang + sway) * limb.len * 0.6;
    ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y,
      x + Math.cos(limb.ang + sway) * limb.len,
      y - trunkHeight * 0.92 + Math.sin(limb.ang + sway) * limb.len * 0.85);
    ctx.stroke();

    // Secondary branches
    for (let b = 0; b < 2; b++) {
      const branchStart = 0.3 + b * 0.35;
      const branchAng = limb.ang + (b === 0 ? -0.5 : 0.6) + Math.sin(t * 0.3 + b) * 0.02;
      const branchLen = limb.len * 0.35;
      ctx.lineWidth = limb.w * 0.45;
      ctx.beginPath();
      ctx.moveTo(
        x + Math.cos(limb.ang + sway) * limb.len * branchStart,
        y - trunkHeight * 0.92 + Math.sin(limb.ang + sway) * limb.len * branchStart * 0.85
      );
      ctx.quadraticCurveTo(
        x + Math.cos(limb.ang + sway) * limb.len * branchStart + Math.cos(branchAng) * branchLen * 0.4,
        y - trunkHeight * 0.92 + Math.sin(limb.ang + sway) * limb.len * branchStart * 0.85 + Math.sin(branchAng) * branchLen * 0.3,
        x + Math.cos(limb.ang + sway) * limb.len * branchStart + Math.cos(branchAng) * branchLen,
        y - trunkHeight * 0.92 + Math.sin(limb.ang + sway) * limb.len * branchStart * 0.85 + Math.sin(branchAng) * branchLen * 0.8
      );
      ctx.stroke();
    }
  }

  // Canopy - layered foliage clusters for depth
  const canopyY = y - trunkHeight * 1.05;
  const canopyColors = [
    { color: '#020603', weight: 1.0 },
    { color: '#050c07', weight: 0.85 },
    { color: '#08140a', weight: 0.7 },
    { color: '#0c1a0e', weight: 0.55 },
    { color: '#102012', weight: 0.4 },
  ];

  // Base canopy shape - multiple overlapping ellipses
  const clumps = [
    { dx: -0.58, dy: -0.1, rx: 0.48, ry: 0.38 },
    { dx: -0.2, dy: -0.32, rx: 0.58, ry: 0.45 },
    { dx: 0.3, dy: -0.2, rx: 0.52, ry: 0.4 },
    { dx: 0.55, dy: 0.05, rx: 0.38, ry: 0.3 },
    { dx: -0.05, dy: 0.15, rx: 0.42, ry: 0.32 },
    { dx: -0.45, dy: 0.18, rx: 0.35, ry: 0.26 },
  ];

  // Draw canopy layers back to front
  for (const layer of canopyColors) {
    for (const c of clumps) {
      ctx.beginPath();
      ctx.ellipse(
        x + c.dx * crownR,
        canopyY + c.dy * crownR,
        crownR * c.rx * layer.weight,
        crownR * c.ry * layer.weight,
        0.1,
        0, Math.PI * 2
      );
      ctx.fillStyle = layer.color;
      ctx.globalAlpha = 0.95;
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  // Individual leaves for detail - fewer, more organic
  const leafColors = ['#040a04', '#061007', '#081409', '#0a180c', '#0c1c0e'];
  for (let i = 0; i < 80; i++) {
    const a = (i * 2.399) + Math.sin(t * 0.08 + i) * 0.02;
    const rad = (0.15 + (i % 23) / 25) * crownR;
    const lx = x + Math.cos(a) * rad * 1.2;
    const ly = canopyY + Math.sin(a) * rad * 0.85;
    const len = 6 + (i % 5) * 2;
    const sway = Math.sin(t * 0.3 + i) * 0.08;
    drawLeaf(ctx, lx, ly, len, a + 0.8 + sway, leafColors[i % leafColors.length]);
  }
}

function drawBushesAdvanced(ctx, width, height, bushTop, t) {
  const layers = [
    { y: bushTop + height * 0.15, color: '#030a05', leaf: '#08180c', scale: 1.4, count: 6, depth: 0.9 },
    { y: bushTop + height * 0.08, color: '#051208', leaf: '#0e2812', scale: 1.15, count: 8, depth: 0.7 },
    { y: bushTop + height * 0.01, color: '#081a0c', leaf: '#15381a', scale: 0.95, count: 10, depth: 0.5 },
    { y: bushTop - height * 0.03, color: '#0b2210', leaf: '#1e4a22', scale: 0.75, count: 12, depth: 0.3 },
    { y: bushTop - height * 0.05, color: '#0e2a14', leaf: '#28582c', scale: 0.55, count: 14, depth: 0.15 },
  ];

  layers.forEach((layer) => {
    const step = width / layer.count;
    for (let i = -1; i <= layer.count; i++) {
      const cx = i * step + step / 2 + Math.sin(t * 0.07 + i * 1.8) * 4;
      const w = step * 1.5 * layer.scale;
      const h = height * 0.2 * layer.scale;

      // Bush mound shape with bezier curves
      ctx.beginPath();
      ctx.moveTo(cx - w / 2, layer.y);
      ctx.bezierCurveTo(
        cx - w / 2, layer.y - h * 0.25,
        cx - w / 3, layer.y - h * 1.15,
        cx, layer.y - h
      );
      ctx.bezierCurveTo(
        cx + w / 3, layer.y - h * 1.2,
        cx + w / 2, layer.y - h * 0.25,
        cx + w / 2, layer.y
      );
      ctx.closePath();

      // Depth gradient for each bush
      const bushGrad = ctx.createRadialGradient(cx, layer.y - h * 0.4, 0, cx, layer.y, w * 0.6);
      bushGrad.addColorStop(0, layer.color);
      bushGrad.addColorStop(0.6, layer.color);
      bushGrad.addColorStop(1, '#020602');
      ctx.fillStyle = bushGrad;
      ctx.globalAlpha = 0.95;
      ctx.fill();

      // Bush edge highlight
      ctx.strokeStyle = `rgba(40, 80, 45, ${0.12 * layer.depth})`;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Leaves on each bush
      const leafCount = 18 + Math.round(layer.scale * 12);
      for (let n = 0; n < leafCount; n++) {
        const px = cx + (n / leafCount - 0.5) * w * 0.88;
        const py = layer.y - h * (0.2 + ((n * 11) % 13) / 16);
        const sway = Math.sin(t * 0.45 + n * 0.7 + i * 2) * 0.15;
        drawLeaf(ctx, px, py, 5 + layer.scale * 5, -1.1 + sway + (n % 7) * 0.15, layer.leaf);
      }
    }
  });

  // Ground gradient
  const groundGrad = ctx.createLinearGradient(0, bushTop + height * 0.18, 0, height);
  groundGrad.addColorStop(0, '#040c05');
  groundGrad.addColorStop(0.3, '#030803');
  groundGrad.addColorStop(0.6, '#020502');
  groundGrad.addColorStop(1, '#010301');
  ctx.fillStyle = groundGrad;
  ctx.fillRect(0, bushTop + height * 0.18, width, height - (bushTop + height * 0.18));

  // Ground texture - scattered grass blades
  ctx.strokeStyle = 'rgba(30, 70, 35, 0.18)';
  ctx.lineWidth = 0.8;
  ctx.lineCap = 'round';
  for (let i = 0; i < 40; i++) {
    const gx = (i * 89) % width;
    const gy = bushTop + height * 0.18 + ((i * 67) % (height * 0.15));
    const gh = 4 + (i % 5) * 3;
    const sway = Math.sin(t * 0.6 + i) * 2;
    ctx.beginPath();
    ctx.moveTo(gx, gy + 2);
    ctx.quadraticCurveTo(gx + sway, gy - gh * 0.5, gx + sway * 0.5, gy - gh);
    ctx.stroke();
  }
}

export function createFirefliesStage(canvas, opts = {}) {
  const ctx = canvas.getContext('2d');
  let width = 0;
  let height = 0;
  let dpr = Math.min(window.devicePixelRatio || 1, 2);
  const stars = [];
  const fireflies = new Map();
  let hoverId = null;
  let selectCb = opts.onSelect || null;
  let hoverCb = opts.onHover || null;
  let running = true;
  let rafId = null;
  const startTime = performance.now();
  let lastNow = startTime;

  let bgImage = null;
  let bgImageReady = false;
  if (opts.backgroundImageUrl) {
    bgImage = new Image();
    bgImage.crossOrigin = 'anonymous';
    bgImage.onload = () => { bgImageReady = true; };
    bgImage.src = opts.backgroundImageUrl;
  }

  function drawCoverImage(img) {
    const scale = Math.max(width / img.width, height / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    ctx.drawImage(img, (width - w) / 2, (height - h) / 2, w, h);
    ctx.fillStyle = 'rgba(5, 8, 20, 0.25)';
    ctx.fillRect(0, 0, width, height);
  }

  function resize() {
    const rect = canvas.parentElement.getBoundingClientRect();
    width = Math.max(1, rect.width);
    height = Math.max(1, rect.height);
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    if (stars.length === 0) {
      const rnd = seededRandom(42);
      for (let i = 0; i < 100; i++) {
        stars.push({
          x: rnd(), y: rnd() * 0.5,
          r: 0.4 + rnd() * 1.6,
          phase: rnd() * Math.PI * 2,
          speed: 0.3 + rnd() * 1.0,
          twinkle: rnd() > 0.7,
        });
      }
    }
    layoutFireflies();
  }

  function skyBounds() {
    const pad = 28;
    return {
      left: pad,
      top: pad,
      right: width - pad,
      bottom: height * 0.58,
    };
  }

  function layoutFireflies() {
    const box = skyBounds();
    for (const f of fireflies.values()) {
      if (!f.laidOut) {
        f.x = box.left + f.rnd() * Math.max(1, box.right - box.left);
        f.y = box.top + f.rnd() * Math.max(1, box.bottom - box.top);
        f.laidOut = true;
      }
      f.x = Math.min(box.right, Math.max(box.left, f.x || box.left));
      f.y = Math.min(box.bottom, Math.max(box.top, f.y || box.top));
    }
  }

  function wrapAngle(a) {
    while (a > Math.PI) a -= Math.PI * 2;
    while (a < -Math.PI) a += Math.PI * 2;
    return a;
  }

  function updateFireflies(dt) {
    const box = skyBounds();
    const margin = 46;
    for (const f of fireflies.values()) {
      if (!f.laidOut) continue;
      f.wanderTimer -= dt;
      if (f.wanderTimer <= 0) {
        f.wanderTimer = 0.5 + f.rnd() * 1.8;
        f.targetAngle = f.rnd() * Math.PI * 2;
      }
      if (f.x < box.left + margin) f.targetAngle = 0;
      else if (f.x > box.right - margin) f.targetAngle = Math.PI;
      if (f.y < box.top + margin) f.targetAngle = Math.PI / 2;
      else if (f.y > box.bottom - margin) f.targetAngle = -Math.PI / 2;
      let diff = wrapAngle(f.targetAngle - f.angle);
      f.angle += Math.max(-f.turnRate * dt, Math.min(f.turnRate * dt, diff));
      f.x += Math.cos(f.angle) * f.speed * dt;
      f.y += Math.sin(f.angle) * f.speed * dt;
      f.x = Math.min(box.right, Math.max(box.left, f.x));
      f.y = Math.min(box.bottom, Math.max(box.top, f.y));
    }
  }

  function ensureFirefly(msg) {
    if (fireflies.has(msg.id)) return;
    const seed = hashId(msg.id);
    const rnd = seededRandom(seed);
    fireflies.set(msg.id, {
      id: msg.id,
      sender_name: msg.sender_name,
      message_body: msg.message_body,
      rnd,
      radius: 3.5 + rnd() * 2.0,
      freqX: 0.10 + rnd() * 0.16,
      freqY: 0.14 + rnd() * 0.20,
      phaseX: rnd() * Math.PI * 2,
      phaseY: rnd() * Math.PI * 2,
      pulsePhase: rnd() * Math.PI * 2,
      pulseSpeed: 0.55 + rnd() * 0.85,
      hue: 45 + rnd() * 30,
      spawnTime: performance.now(),
      laidOut: false,
      x: width / 2,
      y: height / 2,
      angle: rnd() * Math.PI * 2,
      targetAngle: rnd() * Math.PI * 2,
      wanderTimer: rnd() * 0.4,
      speed: 22 + rnd() * 32,
      turnRate: 1.1 + rnd() * 1.8,
    });
  }

  function setMessages(messages) {
    for (const msg of messages) ensureFirefly(msg);
    layoutFireflies();
  }

  function drawBackground(t) {
    if (bgImageReady) {
      drawCoverImage(bgImage);
      return;
    }

    // Night sky gradient
    const grad = ctx.createLinearGradient(0, 0, 0, height);
    grad.addColorStop(0, '#03050a');
    grad.addColorStop(0.3, '#050a14');
    grad.addColorStop(0.55, '#081218');
    grad.addColorStop(0.7, '#0a1810');
    grad.addColorStop(1, '#061008');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);

    // Warm moon stays fixed at the top-left
    const moonX = width * 0.13;
    const moonY = height * 0.14;
    const moonR = Math.min(width, height) * 0.09;
    drawMoonWithCraters(ctx, moonX, moonY, moonR);

    // Stars with twinkling
    for (const s of stars) {
      let alpha = 0.25 + 0.6 * (0.5 + 0.5 * Math.sin(t * s.speed + s.phase));
      if (s.twinkle) alpha *= 0.5 + 0.5 * Math.sin(t * 3 + s.phase);
      ctx.beginPath();
      ctx.arc(s.x * width, s.y * height, s.r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,255,${alpha.toFixed(2)})`;
      ctx.fill();
    }

    // Silhouette tree on the left
    const treeX = width * 0.16;
    const treeY = height * 0.98;
    const treeHeight = height * 0.78;
    const treeWidth = width * 0.34;
    drawTree(ctx, treeX, treeY, treeHeight, treeWidth, t);

    // Advanced bushes
    const bushTop = height * 0.6;
    drawBushesAdvanced(ctx, width, height, bushTop, t);
  }

  function drawFirefly(f, t, isHover) {
    const x = f.x;
    const y = f.y;
    f._screenX = x;
    f._screenY = y;

    const age = performance.now() - f.spawnTime;
    const spawnScale = Math.min(1, age / 600);

    const pulseCycle = (t * f.pulseSpeed + f.pulsePhase) % (Math.PI * 2);
    const pulse = pulseCycle < 1.35
      ? 0.12 + 0.88 * Math.sin((pulseCycle / 1.35) * Math.PI)
      : 0.06 + 0.08 * Math.sin(pulseCycle);
    const glowR = (isHover ? 20 : 14) * spawnScale;
    const coreR = f.radius * (isHover ? 1.8 : 1) * spawnScale;

    // Outer glow
    const glow = ctx.createRadialGradient(x, y, 0, x, y, glowR);
    glow.addColorStop(0, `hsla(${f.hue}, 100%, 80%, ${0.6 * pulse})`);
    glow.addColorStop(0.3, `hsla(${f.hue}, 100%, 70%, ${0.3 * pulse})`);
    glow.addColorStop(1, `hsla(${f.hue}, 100%, 60%, 0)`);
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(x, y, glowR, 0, Math.PI * 2);
    ctx.fill();

    // Inner glow
    const innerGlow = ctx.createRadialGradient(x, y, 0, x, y, coreR * 2);
    innerGlow.addColorStop(0, `hsla(${f.hue}, 100%, 95%, ${0.9 * pulse})`);
    innerGlow.addColorStop(1, `hsla(${f.hue}, 100%, 85%, ${0.4 * pulse})`);
    ctx.fillStyle = innerGlow;
    ctx.beginPath();
    ctx.arc(x, y, coreR * 1.5, 0, Math.PI * 2);
    ctx.fill();

    // Core
    ctx.beginPath();
    ctx.arc(x, y, coreR, 0, Math.PI * 2);
    ctx.fillStyle = `hsla(${f.hue}, 100%, ${90 + pulse * 10}%, 1)`;
    ctx.fill();

    // Trailing particles
    if (isHover || pulse > 0.8) {
      for (let i = 0; i < 3; i++) {
        const trailX = x - Math.cos(f.angle) * (6 + i * 5);
        const trailY = y - Math.sin(f.angle) * (6 + i * 5);
        const trailR = coreR * (0.6 - i * 0.15) * (1 - i * 0.2);
        ctx.beginPath();
        ctx.arc(trailX, trailY, trailR, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${f.hue}, 100%, 85%, ${0.4 * pulse * (1 - i * 0.3)})`;
        ctx.fill();
      }
    }

    if (isHover) {
      ctx.beginPath();
      ctx.arc(x, y, glowR * 1.2, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(255,255,255,0.5)';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }

  function frame(now) {
    if (!running) return;
    const dt = Math.min(0.05, (now - lastNow) / 1000);
    lastNow = now;
    const t = (now - startTime) / 1000;
    updateFireflies(dt);
    drawBackground(t);
    for (const f of fireflies.values()) {
      drawFirefly(f, t, f.id === hoverId);
    }
    rafId = requestAnimationFrame(frame);
  }

  function hitTest(clientX, clientY) {
    const rect = canvas.getBoundingClientRect();
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    let found = null;
    let bestDist = Infinity;
    for (const f of fireflies.values()) {
      const dx = (f._screenX ?? f.x) - px;
      const dy = (f._screenY ?? f.y) - py;
      const dist = Math.hypot(dx, dy);
      const hitR = Math.max(18, f.radius * 4);
      if (dist <= hitR && dist < bestDist) {
        bestDist = dist;
        found = f;
      }
    }
    return found ? { obj: found, x: px, y: py } : null;
  }

  function handleMove(e) {
    const hit = hitTest(e.clientX, e.clientY);
    const id = hit ? hit.obj.id : null;
    if (id !== hoverId) {
      hoverId = id;
      canvas.style.cursor = id ? 'pointer' : 'default';
      if (hoverCb) hoverCb(hit ? { id: hit.obj.id, sender_name: hit.obj.sender_name, message_body: hit.obj.message_body, x: hit.x, y: hit.y } : null);
    } else if (hit && hoverCb) {
      hoverCb({ id: hit.obj.id, sender_name: hit.obj.sender_name, message_body: hit.obj.message_body, x: hit.x, y: hit.y });
    }
  }

  function handleClick(e) {
    const hit = hitTest(e.clientX, e.clientY);
    if (hit && selectCb) {
      selectCb({ id: hit.obj.id, sender_name: hit.obj.sender_name, message_body: hit.obj.message_body }, { x: hit.x, y: hit.y });
    }
  }

  function handleLeave() {
    if (hoverId !== null) {
      hoverId = null;
      canvas.style.cursor = 'default';
      if (hoverCb) hoverCb(null);
    }
  }

  canvas.addEventListener('pointermove', handleMove);
  canvas.addEventListener('click', handleClick);
  canvas.addEventListener('pointerleave', handleLeave);

  resize();
  rafId = requestAnimationFrame(frame);

  return {
    setMessages,
    onSelect(cb) { selectCb = cb; },
    onHover(cb) { hoverCb = cb; },
    resize,
    destroy() {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      canvas.removeEventListener('pointermove', handleMove);
      canvas.removeEventListener('click', handleClick);
      canvas.removeEventListener('pointerleave', handleLeave);
    },
  };
}